import AssignmentInd from '@mui/icons-material/AssignmentInd';
import CancelIcon from '@mui/icons-material/Cancel';
import Clear from '@mui/icons-material/Clear';
import PersonAdd from '@mui/icons-material/PersonAdd';
import SaveIcon from '@mui/icons-material/Save';
import Warning from '@mui/icons-material/Warning';
import {
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Radio,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    Typography
} from '@mui/material';
import { action, computed, makeObservable, observable, runInAction, toJS } from 'mobx';
import { inject, observer } from 'mobx-react';

import '../../css/botao.css';
import AutoComplete from '../AutoComplete';
import ReactComponent from '../ReactComponent';
import BotaoAlternarSigla from './BotaoAlternarSigla';
import { renderChipOrigemSigla, renderDialogRemocao } from './ComposicaoRenders';
import ComposicaoUtil from './ComposicaoUtil';

class ModalComposicaoPauta extends ReactComponent {
    openComposicao = false;
    sessao;
    composicao = [];
    magistradoParaAdicionar = null;
    magistradosAdicionados = new Set();
    dialogRemocaoAberto = false;
    fraseDialogRemocao = '';
    magistradoParaRemover = null;

    handleClickGravarComposicao = async () => {
        const mapaAutoComplete = new Map(
            this.dominioStore.opcoesMagistradosAutoComplete().map((m) => [m.valor?.cpf, m.valor])
        );

        const composicaoLimpa = this.composicao.map((item) => {
            const itemJs = toJS(item);
            const t = itemJs.titular || {};
            const auto = mapaAutoComplete.get(t.cpf) || {};

            const siglaAdministrativa = t.siglaAdministrativa || auto.siglaAdministrativa || '';
            const siglaExibicao = t.sigla || auto.sigla || '';
            const siglaParaGravar = siglaAdministrativa || siglaExibicao;

            return {
                titular: {
                    id: t.id ?? auto.id ?? null,
                    sigla: siglaParaGravar,
                    nome: t.nome || auto.nome || '',
                    genero: t.genero || auto.genero || '',
                    codigoCargo: t.codigoCargo || auto.codigoCargo || '',
                    cpf: t.cpf || auto.cpf || ''
                },
                presidenteSessao: Boolean(itemJs.presidenteSessao)
            };
        });

        const resultado = await this.sessaoService.gravarComposicaoPadraoSessao(
            this.observableProps.sessaoId,
            composicaoLimpa
        );

        if (resultado.status === 204) {
            this.magistradosAdicionados.clear();
            this.mensagemStore.abrirMensagemSucesso('Composição alterada com sucesso');
            this.fecharDialog();
        }
    };

    abrirDialog = async () => {
        await this.carregarComposicao();
        runInAction(() => (this.openComposicao = true));

        if (this.sessao && this.sessao.quantidadeTotalProcessos > 0) {
            this.mensagemStore.abrirMensagemInfo(
                'Remoção de magistrados da composição padrão não é permitida em pauta com processos'
            );
        }
    };

    fecharDialog = () => {
        this.openComposicao = false;
    };

    toggleMagistradoPresidente = (magistrado) => {
        if (!this.alteracaoPermitida) {
            this.mensagemStore.abrirMensagemInfo(
                `A alteração do presidente passará a valer para processos incluídos na pauta a partir de agora.
                Processos que já estão incluídos continuarão com o presidente antigo.`
            );
        }
        for (const magistradoComposicao of this.composicao) {
            magistradoComposicao.presidenteSessao = false;
        }
        magistrado.presidenteSessao = true;
    };

    handleClickRemoverMagistrado = (magistrado) => {
        if (magistrado.presidenteSessao) {
            this.fraseDialogRemocao = magistrado.titular.nome + ' é presidente da sessão. Confirma remoção?';
            this.magistradoParaRemover = magistrado.titular;
            this.abrirDialogoRemocao();
        } else {
            this.removerMagistrado(magistrado.titular);
        }
    };

    abrirDialogoRemocao = () => {
        this.dialogRemocaoAberto = true;
    };

    fecharDialogoRemocao = () => {
        this.dialogRemocaoAberto = false;
        this.magistradoParaRemover = null;
    };

    removerMagistrado = (magistrado) => {
        if (!magistrado) {
            this.fecharDialogoRemocao();
            return;
        }

        const index = this.composicao.findIndex(
            (magistradoComposicao) =>
                magistradoComposicao.titular.sigla === magistrado.sigla &&
                magistradoComposicao.titular.cpf === magistrado.cpf
        );

        if (index !== -1) {
            this.composicao.splice(index, 1);
        }

        this.fecharDialogoRemocao();
    };

    callbackAutoCompleteAddMagistrado = (option) => {
        this.magistradoParaAdicionar = option ? option.valor : null;
    };

    adicionarMagistrado = () => {
        if (this.validarLocalizacaoMagistrado()) {
            if (
                this.isComponenteHabilitado('ConsultaComposicaoPadrao') &&
                !this.isMagistradoNaComposicaoPadraoOJC(this.magistradoParaAdicionar)
            ) {
                const siglaMagistrado = this.magistradoParaAdicionar?.sigla;
                const msg = siglaMagistrado
                    ? `Atenção: O magistrado "${siglaMagistrado}" não está na composição padrão do OJC`
                    : `Atenção: O magistrado não está na composição padrão do OJC`;

                this.mensagemStore.abrirMensagemAlerta(msg);
            }

            this.composicao.push({ titular: { ...toJS(this.magistradoParaAdicionar) } });
            this.magistradosAdicionados.add(this.magistradoParaAdicionar.id);
            this.magistradoParaAdicionar = null;

            ComposicaoUtil.rolarParaFim('#tBodyComposicaoPauta');
        } else {
            this.mensagemStore.abrirMensagemAlerta(
                'Não é possível inserir na composição Magistrado sem localização no OJC'
            );
        }
    };

    alternarSigla = (magistrado, proxima) => {
        const item = this.composicao.find(
            (comp) => comp.titular.cpf === magistrado.cpf && comp.titular.sigla === magistrado.sigla
        );

        if (item) {
            const idOriginal = item.titular?.id;
            item.titular = {
                ...toJS(proxima),
                id: proxima?.id || idOriginal
            };
        }
    };

    constructor(props) {
        super(props);

        makeObservable(this, {
            alternarSigla: action,
            carregarComposicao: false,
            ajustarComposicao: false,
            validarLocalizacaoMagistrado: false,
            isMagistradoNaComposicaoPadraoOJC: false,
            titularesAtuais: false,
            tooltipBotaoGravar: false,
            render: false,
            renderAcoesAlteracaoPermitida: false,
            openComposicao: observable,
            sessao: observable,
            composicao: observable,
            magistradoParaAdicionar: observable,
            magistradosAdicionados: observable,
            dialogRemocaoAberto: observable,
            fraseDialogRemocao: observable,
            magistradoParaRemover: observable,
            alteracaoPermitida: computed,
            handleClickGravarComposicao: action,
            abrirDialog: action,
            fecharDialog: action,
            toggleMagistradoPresidente: action,
            removerMagistrado: action,
            handleClickRemoverMagistrado: action,
            abrirDialogoRemocao: action,
            fecharDialogoRemocao: action,
            callbackAutoCompleteAddMagistrado: action,
            adicionarMagistrado: action,
            opcoesMagistradosAdicionar: computed,
            quorumAlcancado: computed,
            renderConteudoComposicao: computed,
            renderDialogRemocao: computed,
            existePresidenteNaComposicao: computed
        });
    }

    get alteracaoPermitida() {
        return !this.sessao?.quantidadeTotalProcessos;
    }

    async carregarComposicao() {
        const sessao = await this.sessaoService.recuperarPautaJulgamento(this.observableProps.sessaoId);
        runInAction(() => (this.sessao = sessao));

        if (sessao) {
            if (sessao.composicaoPadrao) {
                runInAction(() => (this.composicao = this.ajustarComposicao(sessao.composicaoPadrao)));
            } else {
                runInAction(
                    () => (this.composicao = this.ajustarComposicao(toJS(this.sessaoStore.composicaoPadraoOJC)))
                );
            }
        }
    }

    ajustarComposicao(composicao) {
        return composicao.filter(
            (magistrado) => magistrado?.titular?.cpf && magistrado.titular.nome && magistrado.titular.sigla
        );
    }

    validarLocalizacaoMagistrado() {
        return (
            !this.sessaoStore.isSessaoPJe(this.sessao.id) ||
            this.sessaoStore.validarLocalizacaoMagistrado(
                this.magistradoParaAdicionar,
                this.sessao.orgaoJudicante.sigla
            )
        );
    }

    isMagistradoNaComposicaoPadraoOJC(magistrado) {
        return this.sessaoStore.composicaoPadraoOJC.some(
            (magistradoComposicao) => magistradoComposicao.titular.sigla === magistrado.sigla
        );
    }

    get opcoesMagistradosAdicionar() {
        const SIGLAS_ADMINISTRATIVAS = ['GCG', 'GP', 'GVP', 'CSP', 'CSCG', 'CSVP'];
        const opcoes = this.dominioStore.opcoesMagistradosAutoComplete();
        const titularesAtuais = this.titularesAtuais(SIGLAS_ADMINISTRATIVAS);

        const siglasJaAdicionadas = new Set();
        const filtradas = [];

        for (const opcao of opcoes) {
            const magistrado = opcao.valor;

            if (!magistrado) continue;

            if (magistrado.sigla === magistrado.siglaAdministrativa) {
                filtradas.push(opcao);
                continue;
            }

            const isCargoAdministrativo = SIGLAS_ADMINISTRATIVAS.includes(magistrado.siglaAdministrativa);
            const isTitularAtual = titularesAtuais[magistrado.siglaAdministrativa] === magistrado.sigla;

            if (!isCargoAdministrativo || !isTitularAtual || siglasJaAdicionadas.has(magistrado.siglaAdministrativa)) {
                continue;
            }
            siglasJaAdicionadas.add(magistrado.siglaAdministrativa);

            const sufixo = `(como ${magistrado.siglaAdministrativa})`;
            const textoBase = opcao.texto || magistrado.sigla;
            const texto = textoBase.includes(sufixo) ? textoBase : `${textoBase} ${sufixo}`;

            filtradas.push({ ...opcao, texto });
        }

        return filtradas.filter((opcao) => this.composicao.every((c) => c.titular.sigla !== opcao.valor.sigla));
    }

    titularesAtuais(siglasAdministrativas) {
        const titulares = {};

        if (!this.parametroStore) return titulares;

        try {
            const todosParametros = Object.values(this.parametroStore)
                .filter(
                    (propriedade) =>
                        Array.isArray(propriedade) && propriedade.length > 0 && Object.hasOwn(propriedade[0], 'chave')
                )
                .flat();

            const datasVazias = new Set([null, undefined, '']);

            for (const chave of siglasAdministrativas) {
                const atual = todosParametros.find((p) => p.chave === chave && datasVazias.has(p.dataFimVigencia));

                if (atual) titulares[chave] = atual.valor || atual.txtValor;
            }
        } catch (error) {
            console.error('Erro ao ler a parametroStore:', error);
        }

        return titulares;
    }

    get quorumAlcancado() {
        return this.composicao && this.sessao && this.composicao.length >= this.sessao.quorum;
    }

    get existePresidenteNaComposicao() {
        return !!this.composicao?.some((c) => c?.presidenteSessao);
    }

    get renderDialogRemocao() {
        return renderDialogRemocao({
            aberto: this.dialogRemocaoAberto,
            frase: this.fraseDialogRemocao,
            onFechar: this.fecharDialogoRemocao,
            onRemover: () => this.removerMagistrado(this.magistradoParaRemover)
        });
    }

    tooltipBotaoGravar() {
        return this.existePresidenteNaComposicao ? '' : 'Defina o presidente para prosseguir';
    }

    render() {
        return (
            <>
                <Tooltip
                    slotProps={{
                        popper: { style: { zIndex: '99999' } }
                    }}
                    placement="top"
                    title="Editar composição padrão da pauta"
                >
                    <IconButton
                        onClick={() => this.abrirDialog()}
                        size="large"
                    >
                        <AssignmentInd />
                    </IconButton>
                </Tooltip>
                {this.openComposicao ? (
                    <Dialog
                        open
                        maxWidth="xl"
                        onClose={this.fecharDialog}
                    >
                        <DialogTitle sx={{ paddingBottom: '6px' }}>
                            Editar composição padrão da pauta {this.sessao.descricao}
                            {!this.quorumAlcancado && (
                                <div
                                    style={{ display: 'flex', alignItems: 'center', width: '480px', margin: '8px 0px' }}
                                >
                                    <Warning sx={{ fill: 'darkorange', marginRight: '8px' }} />
                                    <Typography
                                        sx={{ color: 'darkorange' }}
                                        variant="body2"
                                    >
                                        O número de participantes da composição é menor do que o mínimo necessário para
                                        julgamento
                                    </Typography>
                                </div>
                            )}
                        </DialogTitle>
                        <DialogContent sx={{ paddingBottom: '6px' }}>
                            {this.renderConteudoComposicao}
                            {this.renderDialogRemocao}
                        </DialogContent>
                        <DialogActions sx={{ margin: '0px 16px' }}>
                            {this.renderAcoesAlteracaoPermitida()}
                        </DialogActions>
                    </Dialog>
                ) : null}
            </>
        );
    }

    renderAcoesAlteracaoPermitida() {
        return (
            <>
                <div style={{ width: '250px' }}>
                    <AutoComplete
                        apresentarTextoSecundario
                        label="Selecione..."
                        onChange={this.callbackAutoCompleteAddMagistrado}
                        options={this.opcoesMagistradosAdicionar}
                        value={this.magistradoParaAdicionar ? this.magistradoParaAdicionar.sigla : null}
                    />
                </div>
                <Tooltip title="Adicionar magistrado">
                    <div>
                        <IconButton
                            color={'primary'}
                            disabled={!this.magistradoParaAdicionar}
                            onClick={this.adicionarMagistrado}
                            size="large"
                        >
                            <PersonAdd />
                        </IconButton>
                    </div>
                </Tooltip>
                <Button
                    color="secondary"
                    endIcon={<CancelIcon />}
                    onClick={this.fecharDialog}
                    variant="outlined"
                >
                    Cancelar
                </Button>
                <Tooltip title={this.tooltipBotaoGravar()}>
                    <span>
                        <Button
                            color="primary"
                            disabled={!this.existePresidenteNaComposicao}
                            endIcon={<SaveIcon />}
                            onClick={this.handleClickGravarComposicao}
                            variant="outlined"
                        >
                            Gravar
                        </Button>
                    </span>
                </Tooltip>
            </>
        );
    }

    get renderConteudoComposicao() {
        return (
            <Table sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100% - 32px)' }}>
                <TableHead>
                    <TableRow sx={{ width: '100%' }}>
                        <TableCell
                            align="center"
                            sx={{ width: '170px' }}
                        >
                            Magistrado
                        </TableCell>
                        <TableCell
                            align="center"
                            sx={{ width: '170px' }}
                        >
                            Presidente
                        </TableCell>
                        <TableCell sx={{ width: '32px' }} />
                    </TableRow>
                </TableHead>
                <TableBody
                    id="tBodyComposicaoPauta"
                    sx={{ flex: 1, overflowY: 'auto' }}
                >
                    {this.composicao.length > 0 &&
                        this.composicao.map((magistrado) => {
                            const entradaAtual = ComposicaoUtil.getEntradaDominioAtual(
                                magistrado.titular,
                                this.dominioStore
                            );
                            return (
                                <TableRow key={magistrado.titular.cpf ?? magistrado.titular.sigla}>
                                    <TableCell
                                        align="center"
                                        sx={{ width: '170px' }}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                            <Tooltip
                                                slotProps={{
                                                    popper: { style: { zIndex: 9_999_999 } }
                                                }}
                                                placement="top"
                                                title={magistrado.titular.nome}
                                            >
                                                <Chip
                                                    label={magistrado.titular.sigla}
                                                    sx={{ minWidth: '10ch', height: '24px' }}
                                                />
                                            </Tooltip>
                                            {renderChipOrigemSigla(entradaAtual)}
                                            <BotaoAlternarSigla
                                                exibir={
                                                    Boolean(this.sessao) && this.sessaoStore.isSessaoPJe(this.sessao.id)
                                                }
                                                onConfirmar={(proxima) =>
                                                    this.alternarSigla(magistrado.titular, proxima)
                                                }
                                                dominioStore={this.dominioStore}
                                                magistrado={magistrado.titular}
                                            />
                                        </div>
                                    </TableCell>
                                    <TableCell
                                        align="center"
                                        sx={{ width: '170px' }}
                                    >
                                        <Radio
                                            checked={magistrado.presidenteSessao === true}
                                            onChange={() => this.toggleMagistradoPresidente(magistrado)}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Tooltip
                                            placement="top"
                                            title="Remover"
                                        >
                                            <IconButton
                                                disabled={
                                                    !this.alteracaoPermitida &&
                                                    !this.magistradosAdicionados.has(magistrado.titular.id)
                                                }
                                                onClick={() => this.handleClickRemoverMagistrado(magistrado)}
                                                size="large"
                                                sx={{ cursor: 'pointer', padding: '4px' }}
                                            >
                                                <Clear />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                </TableBody>
            </Table>
        );
    }
}

export default inject('stores')(observer(ModalComposicaoPauta));
